import {
  renderIssueTemplatesRuleContent,
  ResolvedIssueTemplates,
  resolveIssueTemplates,
} from "./issue-templates";
import { renderGithubIssueTypeSectionLines } from "./issue-type";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import {
  renderProgressFilesRuleContent,
  ResolvedProgressFiles,
  resolveProgressFiles,
} from "./progress-files";
import {
  DEFAULT_SHARED_INDEX_PATHS,
  renderSharedEditingRuleContent,
  ResolvedSharedEditing,
  resolveSharedEditing,
} from "./shared-editing";
import {
  renderSkillEvalsRuleContent,
  ResolvedSkillEvals,
  resolveSkillEvals,
} from "./skill-evals";
import {
  renderTemporalFramingRuleContent,
  ResolvedTemporalFraming,
  resolveTemporalFraming,
} from "./temporal-framing";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE, AgentSkill } from "../types";

const createPackageSkill: AgentSkill = {
  name: "create-package",
  description:
    "Scaffold a new TypeScriptProject sub-project under packages/@scope/<name> by emitting the projen block to add to the monorepo config",
  disableModelInvocation: true,
  instructions: [
    "# Create Package",
    "",
    "Scaffold a new shared-library sub-project at",
    "`packages/@scope/<name>` by emitting the projen block to add to the",
    "monorepo's projen configuration. This skill walks the user through",
    "naming, metadata, and placement; it does **not** run projen, install",
    "dependencies, or execute any build commands.",
    "",
    "## Usage",
    "",
    "```",
    "/create-package @<scope>/<name>",
    "```",
    "",
    "Example: `/create-package @codedrifters/my-lib` → scaffolds a new",
    "`TypeScriptProject` at `packages/@codedrifters/my-lib`.",
    "",
    "## Steps",
    "",
    "1. **Parse and validate the package name.** The argument must be a",
    "   **scoped** npm name in the form `@<scope>/<name>`:",
    "   - Starts with `@`",
    "   - Has exactly one `/` separator",
    "   - `<scope>` and `<name>` are lowercase, kebab-case, non-empty",
    "   - Unscoped names (e.g. `my-lib`) are **not** allowed — every",
    "     package under `packages/` is scoped by owner.",
    "   If the input is missing or invalid, ask the user to supply a",
    "   scoped name. Do not guess the scope.",
    "",
    "2. **Collect metadata** — ask the user for each of the following,",
    "   one question at a time:",
    "   - **Description** — one-line summary of what the package does.",
    "   - **Publish to npm?** — `true` or `false`. Workspace-internal",
    "     libraries should answer `false`.",
    "   Skip any question the user has already answered in the initial",
    "   prompt.",
    "",
    "3. **Determine the target outdir.** The sub-project lives at:",
    "",
    "   ```",
    "   packages/<scope>/<name>",
    "   ```",
    "",
    "   `<scope>` is everything after `@` and before `/` in the package",
    "   name; `<name>` is everything after the `/`. This matches the",
    "   default `outdir` that `TypeScriptProject` computes from the",
    "   package name, so the emitted block does **not** need to set",
    "   `outdir` explicitly.",
    "",
    "4. **Locate the projen config file.** Prefer the monorepo's",
    "   `projenrc/` directory — most monorepos declare sub-projects",
    "   there (e.g. `projenrc/root-project.ts` or a dedicated",
    "   `projenrc/packages.ts`). Fall back to `.projenrc.ts` only when",
    "   no `projenrc/` directory exists.",
    "",
    "5. **Emit the projen block.** Show the TypeScript block to paste",
    "   into the chosen projen config file. Example for",
    "   `@codedrifters/my-lib`:",
    "",
    "   ```typescript",
    "   import { TypeScriptProject } from '@codedrifters/configulator';",
    "",
    "   // Inside the monorepo's configuration function, alongside",
    "   // sibling sub-projects:",
    "   new TypeScriptProject({",
    "     parent: monorepo,",
    "     name: '@codedrifters/my-lib',",
    "     description: '<one-line description>',",
    "     defaultReleaseBranch: 'main',",
    "     publishToNpm: false, // or true if this package publishes",
    "   });",
    "   ```",
    "",
    "   Substitute the user's scope, name, description, and",
    "   `publishToNpm` value. Do not hard-code `outdir` — the default",
    "   places the sub-project at `packages/<scope>/<name>`.",
    "",
    "6. **Tell the user how to synthesize.** Instruct them to run, from",
    "   the repo root, the safe three-step regen sequence:",
    "",
    "   ```",
    "   pnpm i",
    "   pnpm exec projen",
    "   pnpm i",
    "   ```",
    "",
    "   The leading `pnpm i` syncs `node_modules` with `pnpm-lock.yaml`",
    "   so synth runs against the right configulator/projen/plugin",
    "   versions — without it, `pnpm exec projen` may synth against a",
    "   stale resolved version of any pnpm-managed component and produce",
    "   phantom drift in generated files. `pnpm exec projen` regenerates",
    "   the sub-project tree; the trailing `pnpm i` updates the workspace",
    "   lockfile to pick up any dependency changes projen wrote during",
    "   synth. Do **not** run these commands yourself — the user runs them.",
    "",
    "## Guardrails",
    "",
    "- Never run `pnpm exec projen`, `pnpm install`, `pnpm i`, `pnpm build`,",
    "  `pnpm test`, or any other package-manager, build, or test command.",
    "  Emit the projen block and instructions only.",
    "- Never scaffold outside `packages/`. Deployable apps belong under",
    "  `apps/<scope>/<name>` (use `/create-app`); user-facing sites belong",
    "  under `sites/<scope>/<name>` (use `/create-site`).",
    "- Never scope by category (e.g. `@libs/foo`, `@packages/foo`). The",
    "  scope identifies the **owning party**; the top-level folder",
    "  identifies the kind.",
    "- Do not invent an `outdir` — rely on the `TypeScriptProject`",
    "  default that places the sub-project at `packages/<scope>/<name>`.",
    "",
    "## Related Skills",
    "",
    "- `/create-app` — scaffold a new `AwsCdkProject` under",
    "  `apps/<scope>/<name>`.",
    "- `/create-site` — scaffold a new `AstroProject` under",
    "  `sites/<scope>/<name>`.",
  ].join("\n"),
};

const createAppSkill: AgentSkill = {
  name: "create-app",
  description:
    "Scaffold a new AwsCdkProject sub-project under apps/@scope/<name> by emitting the projen block to add to the monorepo config",
  disableModelInvocation: true,
  instructions: [
    "# Create App",
    "",
    "Scaffold a new deployable-application sub-project at",
    "`apps/@scope/<name>` by emitting the projen block to add to the",
    "monorepo's projen configuration. This skill walks the user through",
    "naming, metadata, and placement; it does **not** run projen, install",
    "dependencies, or execute any build commands.",
    "",
    "Today this skill scaffolds AWS CDK applications (`AwsCdkProject`).",
    "The `apps/` folder is also the right home for other deployable",
    "application types (mobile apps, backend services) as configulator",
    "grows support for them — but at the moment, this skill emits an",
    "`AwsCdkProject` block.",
    "",
    "## Usage",
    "",
    "```",
    "/create-app @<scope>/<name>",
    "```",
    "",
    "Example: `/create-app @codedrifters/my-stack` → scaffolds a new",
    "`AwsCdkProject` at `apps/@codedrifters/my-stack`.",
    "",
    "## Steps",
    "",
    "1. **Parse and validate the app name.** The argument must be a",
    "   **scoped** npm name in the form `@<scope>/<name>`:",
    "   - Starts with `@`",
    "   - Has exactly one `/` separator",
    "   - `<scope>` and `<name>` are lowercase, kebab-case, non-empty",
    "   - Unscoped names (e.g. `my-stack`) are **not** allowed — every",
    "     app under `apps/` is scoped by owner.",
    "   If the input is missing or invalid, ask the user to supply a",
    "   scoped name. Do not guess the scope.",
    "",
    "2. **Collect metadata** — ask the user for each of the following,",
    "   one question at a time:",
    "   - **Description** — one-line summary of what the app does.",
    "   Skip any question the user has already answered in the initial",
    "   prompt.",
    "",
    "   Do **not** ask for a CDK version. `AwsCdkProject` renders",
    "   `aws-cdk-lib` and `constructs` as `catalog:`, so the monorepo's",
    "   pnpm catalog pins both for every app. Passing a `cdkVersion` or",
    "   `constructsVersion` that disagrees with the catalog throws at",
    "   synth time — a version bump is a catalog edit, not a per-app",
    "   option.",
    "",
    "3. **Determine the target outdir.** The sub-project lives at:",
    "",
    "   ```",
    "   apps/<scope>/<name>",
    "   ```",
    "",
    "   `<scope>` is everything after `@` and before `/` in the app",
    "   name; `<name>` is everything after the `/`. This matches the",
    "   default `outdir` that `AwsCdkProject` computes from the package",
    "   name, so the emitted block does **not** need to set `outdir`",
    "   explicitly.",
    "",
    "4. **Locate the projen config file.** Prefer the monorepo's",
    "   `projenrc/` directory — most monorepos declare sub-projects",
    "   there (e.g. `projenrc/root-project.ts` or a dedicated",
    "   `projenrc/apps.ts`). Fall back to `.projenrc.ts` only when",
    "   no `projenrc/` directory exists.",
    "",
    "5. **Emit the projen block.** Show the TypeScript block to paste",
    "   into the chosen projen config file. Example for",
    "   `@codedrifters/my-stack`:",
    "",
    "   ```typescript",
    "   import { AwsCdkProject } from '@codedrifters/configulator';",
    "",
    "   // Inside the monorepo's configuration function, alongside",
    "   // sibling sub-projects:",
    "   new AwsCdkProject({",
    "     parent: monorepo,",
    "     name: '@codedrifters/my-stack',",
    "     description: '<one-line description>',",
    "   });",
    "   ```",
    "",
    "   Substitute the user's scope, name, and description. Do not",
    "   hard-code `outdir` — the default places the sub-project at",
    "   `apps/<scope>/<name>` — and do not add `cdkVersion`; the",
    "   catalog supplies it.",
    "",
    "6. **Tell the user how to synthesize.** Instruct them to run, from",
    "   the repo root, the safe three-step regen sequence:",
    "",
    "   ```",
    "   pnpm i",
    "   pnpm exec projen",
    "   pnpm i",
    "   ```",
    "",
    "   The leading `pnpm i` syncs `node_modules` with `pnpm-lock.yaml`",
    "   so synth runs against the right configulator/projen/plugin",
    "   versions — without it, `pnpm exec projen` may synth against a",
    "   stale resolved version of any pnpm-managed component and produce",
    "   phantom drift in generated files. `pnpm exec projen` regenerates",
    "   the sub-project tree; the trailing `pnpm i` updates the workspace",
    "   lockfile to pick up any dependency changes projen wrote during",
    "   synth. Do **not** run these commands yourself — the user runs them.",
    "",
    "## Guardrails",
    "",
    "- Never run `pnpm exec projen`, `pnpm install`, `pnpm i`, `pnpm build`,",
    "  `pnpm test`, or any other package-manager, build, or test command.",
    "  Emit the projen block and instructions only.",
    "- Never scaffold outside `apps/`. Shared libraries belong under",
    "  `packages/<scope>/<name>` (use `/create-package`); user-facing",
    "  sites belong under `sites/<scope>/<name>` (use `/create-site`).",
    "- Never scope by category (e.g. `@apps/foo`, `@stacks/foo`). The",
    "  scope identifies the **owning party**; the top-level folder",
    "  identifies the kind.",
    "- Do not invent an `outdir` — rely on the `AwsCdkProject` default",
    "  that places the sub-project at `apps/<scope>/<name>`.",
    "",
    "## Related Skills",
    "",
    "- `/create-package` — scaffold a new `TypeScriptProject` under",
    "  `packages/<scope>/<name>`.",
    "- `/create-site` — scaffold a new `AstroProject` under",
    "  `sites/<scope>/<name>`.",
  ].join("\n"),
};

const createSiteSkill: AgentSkill = {
  name: "create-site",
  description:
    "Scaffold a new AstroProject sub-project under sites/@scope/<name> by emitting the projen block to add to the monorepo config",
  disableModelInvocation: true,
  instructions: [
    "# Create Site",
    "",
    "Scaffold a new user-facing web front end at",
    "`sites/@scope/<name>` by emitting the projen block to add to the",
    "monorepo's projen configuration. This skill walks the user through",
    "naming, metadata, and placement; it does **not** run projen, install",
    "dependencies, or execute any build commands.",
    "",
    "The default project type is `AstroProject` — the common shape for",
    "marketing sites, SPAs, and client-facing static sites. A",
    '`StarlightProject` with `role: "site"` is also a valid placement',
    "under `sites/<scope>/<name>` for consumers who specifically want a",
    "Starlight-based front end; it is **not** the monorepo-wide docs site.",
    "",
    "## Usage",
    "",
    "```",
    "/create-site @<scope>/<name>",
    "```",
    "",
    "Example: `/create-site @codedrifters/marketing-site` → scaffolds a",
    "new `AstroProject` at `sites/@codedrifters/marketing-site`.",
    "",
    "## Docs-vs-Site Guard",
    "",
    "There is **exactly one** Starlight docs site per monorepo, and it",
    "lives at `/docs` — not under `sites/`. Before emitting any block,",
    "check whether the user actually wants the monorepo-wide docs site:",
    "",
    "- If the user's intent is to create the single dev-docs site for the",
    "  monorepo (names like `docs`, `documentation`, `dev-docs`, or any",
    '  explicit mention of "the docs site"), **refuse** to scaffold it',
    "  under `sites/`. Redirect them to the `StarlightProject` defaults",
    "  that place the docs site at `/docs`, and explain that `sites/` is",
    "  reserved for end-user-facing web front ends (marketing, SPAs,",
    "  client-facing apps).",
    "- If the user wants a user-facing site that happens to use Starlight",
    "  (e.g. a public-facing documentation portal for a specific",
    "  product), that is fine — they should use `StarlightProject` with",
    '  `role: "site"`, which routes under `sites/<scope>/<name>`.',
    "- Otherwise, proceed with `AstroProject` as the default.",
    "",
    "## Steps",
    "",
    "1. **Parse and validate the site name.** The argument must be a",
    "   **scoped** npm name in the form `@<scope>/<name>`:",
    "   - Starts with `@`",
    "   - Has exactly one `/` separator",
    "   - `<scope>` and `<name>` are lowercase, kebab-case, non-empty",
    "   - Unscoped names (e.g. `marketing-site`) are **not** allowed —",
    "     every site under `sites/` is scoped by owner.",
    "   If the input is missing or invalid, ask the user to supply a",
    "   scoped name. Do not guess the scope. Run the docs-vs-site guard",
    "   above before continuing.",
    "",
    "2. **Collect metadata** — ask the user for each of the following,",
    "   one question at a time:",
    "   - **Description** — one-line summary of what the site is for.",
    "   - **Project type** — `AstroProject` (default) or",
    "     `StarlightProject` (role = site). If the user is unsure,",
    "     recommend `AstroProject` — Starlight is only the right choice",
    "     when the site's primary purpose is a documentation-style",
    '     layout. As an aside: a `StarlightProject` with `role: "site"`',
    "     lives at the same `sites/<scope>/<name>` path, so switching",
    "     between the two later is a low-cost change.",
    "   Skip any question the user has already answered in the initial",
    "   prompt.",
    "",
    "3. **Determine the target outdir.** The sub-project lives at:",
    "",
    "   ```",
    "   sites/<scope>/<name>",
    "   ```",
    "",
    "   `<scope>` is everything after `@` and before `/` in the site",
    "   name; `<name>` is everything after the `/`. This matches the",
    "   default `outdir` that `AstroProject` (and `StarlightProject` with",
    '   `role: "site"`) computes from the package name, so the emitted',
    "   block does **not** need to set `outdir` explicitly.",
    "",
    "4. **Locate the projen config file.** Prefer the monorepo's",
    "   `projenrc/` directory — most monorepos declare sub-projects",
    "   there (e.g. `projenrc/root-project.ts` or a dedicated",
    "   `projenrc/sites.ts`). Fall back to `.projenrc.ts` only when",
    "   no `projenrc/` directory exists.",
    "",
    "5. **Emit the projen block.** Show the TypeScript block to paste",
    "   into the chosen projen config file. Example for",
    "   `@codedrifters/marketing-site` as an `AstroProject`:",
    "",
    "   ```typescript",
    "   import { AstroProject } from '@codedrifters/configulator';",
    "",
    "   // Inside the monorepo's configuration function, alongside",
    "   // sibling sub-projects:",
    "   new AstroProject({",
    "     parent: monorepo,",
    "     name: '@codedrifters/marketing-site',",
    "     description: '<one-line description>',",
    "   });",
    "   ```",
    "",
    '   If the user chose `StarlightProject` with `role: "site"`, emit',
    "   that variant instead — same path, different project class:",
    "",
    "   ```typescript",
    "   import { StarlightProject } from '@codedrifters/configulator';",
    "",
    "   new StarlightProject({",
    "     parent: monorepo,",
    "     name: '@codedrifters/marketing-site',",
    "     description: '<one-line description>',",
    "     role: 'site',",
    "   });",
    "   ```",
    "",
    "   Substitute the user's scope, name, and description. Do not",
    "   hard-code `outdir` — the default places the sub-project at",
    "   `sites/<scope>/<name>`.",
    "",
    "6. **Tell the user how to synthesize.** Instruct them to run, from",
    "   the repo root, the safe three-step regen sequence:",
    "",
    "   ```",
    "   pnpm i",
    "   pnpm exec projen",
    "   pnpm i",
    "   ```",
    "",
    "   The leading `pnpm i` syncs `node_modules` with `pnpm-lock.yaml`",
    "   so synth runs against the right configulator/projen/plugin",
    "   versions — without it, `pnpm exec projen` may synth against a",
    "   stale resolved version of any pnpm-managed component and produce",
    "   phantom drift in generated files. `pnpm exec projen` regenerates",
    "   the sub-project tree; the trailing `pnpm i` updates the workspace",
    "   lockfile to pick up any dependency changes projen wrote during",
    "   synth. Do **not** run these commands yourself — the user runs them.",
    "",
    "## Guardrails",
    "",
    "- Never run `pnpm exec projen`, `pnpm install`, `pnpm i`, `pnpm build`,",
    "  `pnpm test`, or any other package-manager, build, or test command.",
    "  Emit the projen block and instructions only.",
    "- Never scaffold the monorepo-wide docs site under `sites/`. The",
    "  dev-docs site is a singleton at `/docs` and is created once per",
    '  repo via `StarlightProject` with its default `role: "docs"` —',
    "  redirect the user there if they ask for it.",
    "- Never scaffold outside `sites/`. Shared libraries belong under",
    "  `packages/<scope>/<name>` (use `/create-package`); deployable apps",
    "  belong under `apps/<scope>/<name>` (use `/create-app`).",
    "- Never scope by category (e.g. `@sites/foo`, `@web/foo`). The",
    "  scope identifies the **owning party**; the top-level folder",
    "  identifies the kind.",
    "- Do not invent an `outdir` — rely on the `AstroProject` default",
    '  (or the `StarlightProject` default when `role: "site"`) that',
    "  places the sub-project at `sites/<scope>/<name>`.",
    "",
    "## Related Skills",
    "",
    "- `/create-package` — scaffold a new `TypeScriptProject` under",
    "  `packages/<scope>/<name>`.",
    "- `/create-app` — scaffold a new `AwsCdkProject` under",
    "  `apps/<scope>/<name>`.",
  ].join("\n"),
};

const createRuleSkill: AgentSkill = {
  name: "create-rule",
  description:
    "Guide for creating new agent rules in this project using configulator",
  disableModelInvocation: true,
  instructions: [
    "# Create Agent Rule",
    "",
    "Create a new agent rule for the **{{repository.owner}}/{{repository.name}}** project.",
    "",
    "## Steps",
    "",
    "1. **Identify the rule purpose** — ask what behavior the rule should enforce or guide",
    "2. **Choose a scope:**",
    "   - `AGENT_RULE_SCOPE.ALWAYS` — active in all contexts",
    "   - `AGENT_RULE_SCOPE.FILE_PATTERN` — active only when working on matching files (requires `filePatterns`)",
    "3. **Pick a name** — lowercase kebab-case (e.g., `react-conventions`, `api-error-handling`)",
    "4. **Write the content** — clear, actionable markdown instructions",
    "5. **Add to the project config** — add the rule to `AgentConfigOptions.rules` in the Projen config file",
    "",
    "## Rule Template",
    "",
    "```typescript",
    "import { AGENT_RULE_SCOPE } from '@codedrifters/configulator';",
    "",
    "// In your AgentConfig options:",
    "{",
    "  rules: [",
    "    {",
    "      name: 'my-new-rule',",
    "      description: 'What this rule does — used by AI for rule selection',",
    "      scope: AGENT_RULE_SCOPE.ALWAYS, // or FILE_PATTERN",
    "      // filePatterns: ['src/**/*.ts'],  // required for FILE_PATTERN scope",
    "      content: [",
    "        '# Rule Title',",
    "        '',",
    "        '## Guidelines',",
    "        '',",
    "        '- Guideline 1',",
    "        '- Guideline 2',",
    "      ].join('\\n'),",
    "      tags: ['coding'], // optional: for ordering",
    "    },",
    "  ],",
    "}",
    "```",
    "",
    "## Best Practices",
    "",
    "- Keep rules **focused** — one concern per rule",
    '- Use **imperative tone** — "Use X" not "You should use X"',
    "- Include **examples** for complex patterns",
    "- Use `ruleExtensions` to add project-specific content to existing bundle rules instead of replacing them",
  ].join("\n"),
};

/**
 * Fully-resolved settings for the five config-driven convention rules
 * the base bundle owns. Every field is already resolved, so
 * `buildBaseBundle` can seed each rule's final content up front rather
 * than shipping default content that a later pass has to rewrite.
 *
 * Seeding before the rule map exists is what lets `ruleExtensions`
 * appends and consumer-supplied same-name `rules` entries compose with
 * a consumer's convention overrides instead of being clobbered by
 * them. This mirrors the `pr-review-policy` rule, which has always
 * resolved its policy inside `buildPrReviewBundle`.
 */
export interface ResolvedBaseConventions {
  readonly progressFiles: ResolvedProgressFiles;
  readonly sharedEditing: ResolvedSharedEditing;
  readonly temporalFraming: ResolvedTemporalFraming;
  readonly skillEvals: ResolvedSkillEvals;
  readonly issueTemplates: ResolvedIssueTemplates;

  /**
   * Whether any bundle contributing a downstream issue kind survived
   * `excludeBundles`. When false the issue-templates rule renders its
   * disabled stub — the convention has nothing left to enforce.
   */
  readonly hasDownstreamIssueKindBundles: boolean;
}

/**
 * The convention settings the base bundle ships when the consumer
 * supplies no override. Exported so callers can spread a partial
 * override over the documented defaults.
 */
export const DEFAULT_BASE_CONVENTIONS: ResolvedBaseConventions = {
  progressFiles: resolveProgressFiles(),
  sharedEditing: resolveSharedEditing(),
  temporalFraming: resolveTemporalFraming(),
  skillEvals: resolveSkillEvals(),
  issueTemplates: resolveIssueTemplates(),
  hasDownstreamIssueKindBundles: true,
};

/**
 * Base bundle — always included unless `includeBaseRules: false`.
 * Contains project-overview, interaction-style, and general-conventions rules.
 */
export function buildBaseBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  conventions: ResolvedBaseConventions = DEFAULT_BASE_CONVENTIONS,
): AgentRuleBundle {
  return {
    name: "base",
    description:
      "Core rules: project overview, interaction style, and general coding conventions",
    appliesWhen: () => true,
    skills: [
      createPackageSkill,
      createAppSkill,
      createSiteSkill,
      createRuleSkill,
    ],
    rules: [
      {
        name: "project-overview",
        description: "Project context and technology stack overview",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Project Overview",
          "",
          "**Repository:** {{repository.owner}}/{{repository.name}}",
          "**Default branch:** {{repository.defaultBranch}}",
          "",
          "## Important Notes",
          "",
          "- **Never edit generated files** — they are marked with `// ~~ Generated by projen`",
          "- **After modifying Projen configuration**, run the three-step regen sequence: `pnpm i`, then `pnpm exec projen`, then `pnpm i` again. The leading `pnpm i` syncs `node_modules` with the lockfile so synth runs against the right configulator/projen/plugin versions; the trailing `pnpm i` refreshes the lockfile to match anything projen rewrote in `package.json`.",
          "- **Configure dependencies through Projen** — never use `npm install`, `pnpm add`, or `yarn add`. Add them to `deps` or `devDeps` in Projen config.",
          "- **Export from index.ts** to maintain clean public APIs",
          '- **`defaultMode` is opt-in** for the rendered Claude Code `settings.json`. configulator does not set it by default, so the synthesised `settings.json` omits the key unless the consumer opts in. Consumers that run autonomous scheduled-task workers (issue-worker, orchestrator, pr-reviewer, and the analyst/writer family) — which would deadlock on confirmation prompts — opt in by setting `claudeSettings.defaultMode: "dontAsk"` on `AgentConfigOptions`.',
          "",
          "## Repository Layout",
          "",
          "Every monorepo built with `@codedrifters/configulator` uses a fixed",
          "top-level folder layout. Read a sub-project's role directly from its",
          "outdir:",
          "",
          "| Folder | Purpose |",
          "|--------|---------|",
          "| `/docs` | **Single** monorepo-wide Starlight docs site (the one site that lives outside `/sites`). |",
          "| `/apps/@scope/<name>` | Deployable applications (CDK stacks, mobile apps, backend services). |",
          "| `/packages/@scope/<name>` | Shared libraries (published npm packages and workspace-internal libraries). |",
          "| `/sites/@scope/<name>` | User-facing web front ends that are **not** the monorepo-wide docs site. |",
          "",
          "Every sub-project under `apps/`, `packages/`, or `sites/` is named",
          "`@ownerscope/<name>`, where **scope is the owning party** (not a",
          "category). Kind is captured by the top-level folder — never scope by",
          "category (e.g. `@apps/foo`, `@libs/bar`).",
          "",
          "To place a **new** sub-project, always use a scaffolding skill — never",
          "hand-roll the path:",
          "",
          "- `/create-package` — new shared library under `packages/<scope>/<name>`",
          "- `/create-app` — new deployable app under `apps/<scope>/<name>`",
          "- `/create-site` — new web front end under `sites/<scope>/<name>`",
          "",
          "The `monorepo-layout` rule documents the full contract (outdir",
          "defaults per project type, the docs-singleton carve-out, synth-time",
          "enforcement).",
        ].join("\n"),
        tags: ["project"],
      },
      {
        name: "cursor-projen-restrictions",
        description:
          "Cursor must not run projen, build, test, or package-manager commands",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Projen & Development Command Restrictions",
          "",
          "**Never** run any of the following commands. Instead, tell the user which commands to run and why.",
          "",
          "## Prohibited Commands",
          "",
          "- `pnpm exec projen` — synthesize project files",
          "- `pnpm install` / `pnpm i` — install dependencies",
          "- `pnpm build` / `pnpm build:all` — build the project",
          "- `pnpm test` / `pnpm --filter ... test` — run tests",
          "- `pnpm eslint` / `pnpm --filter ... eslint` — run linting",
          "- `pnpm compile` / `pnpm --filter ... compile` — compile packages",
          "- `pnpm reset` / `pnpm reset:all` — reset build artifacts",
          "- Any `vitest`, `tsup`, `rollup`, or `turbo` commands",
          "",
          "## What to Do Instead",
          "",
          "After making changes that need validation, tell the user the specific commands to run:",
          "",
          "1. **After projen config changes** — tell the user to run the three-step regen sequence: `pnpm i && pnpm exec projen && pnpm i`",
          "2. **After source code changes** — tell the user to run `pnpm --filter @codedrifters/<package> test`",
          "3. **After multi-package changes** — tell the user to run `pnpm build:all`",
        ].join("\n"),
        platforms: {
          claude: { exclude: true },
        },
        tags: ["project"],
      },
      {
        name: "reference-documentation",
        description:
          "Consult project rules, documentation, and existing code before answering or making changes",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Reference Documentation",
          "",
          "Before answering questions or making changes, always consult available project context.",
          "",
          "1. **Read project rules and guidelines** — check for agent rules, contribution guides, and coding standards defined in the project.",
          "2. **Review existing code** — understand current patterns, conventions, and architecture before proposing changes. Look at similar files and modules for reference.",
          "3. **Check documentation** — consult README files, inline documentation, and any design documents for relevant context.",
          "4. **Respect established patterns** — follow the conventions already in use rather than introducing new ones without discussion.",
          "5. **Verify before assuming** — if documentation or code conflicts with your assumptions, trust the project sources and ask for clarification when needed.",
        ].join("\n"),
        tags: ["project"],
      },
      {
        name: "interaction-style",
        description:
          "Interaction style — ask questions one at a time and wait for feedback",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Interaction Style Guidelines",
          "",
          "When responding to requests, follow these interaction principles.",
          "",
          "1. **Ask when ambiguous**: When requirements are ambiguous or incomplete, always ask clarifying questions before proceeding. Do not make assumptions about the user's intent — it is better to ask than to guess wrong.",
          "2. **Ask questions one at a time**: When clarification is needed, ask a single question and wait for the user's response before proceeding.",
          "3. **Wait for feedback**: After asking a question, pause and wait for the user's answer before asking additional questions or making assumptions.",
          "4. **Avoid question overload**: Do not ask multiple questions in a single response. If multiple clarifications are needed, prioritize the most important question first.",
          "5. **Progressive clarification**: Once the user answers your first question, you may then ask the next most important question if needed.",
          "6. **Confirm understanding**: After receiving feedback, acknowledge the user's response before proceeding with the next step or question.",
          "7. **Be patient**: Do not rush ahead with assumptions. It is better to ask one clarifying question than to proceed with incorrect assumptions.",
        ].join("\n"),
        tags: ["project"],
      },
      {
        name: "general-conventions",
        description:
          "Code formatting (Prettier/ESLint), import conventions (ES modules, import order), error handling (async/await, no floating promises)",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        filePatterns: ["**/*.ts", "**/*.tsx"],
        content: [
          "# General Conventions",
          "",
          "## Code Formatting",
          "",
          "- Use **Prettier** for formatting (runs automatically on save in VS Code)",
          "- Always use curly braces for control flow, even single-line statements; use multi-line format for complex conditionals",
          "- Prefer `const` over `let`; avoid `var`",
          "- Use trailing commas in multi-line objects/arrays",
          "",
          "### ESLint Rules to Follow",
          "",
          "- `curly`: Always use curly braces (multi-line, consistent)",
          "- `dot-notation`: Use dot notation over bracket notation",
          "- `no-bitwise`: No bitwise operators",
          "- `@typescript-eslint/no-shadow`: No variable shadowing",
          "- `@typescript-eslint/member-ordering`: Follow member order",
          "",
          "## Import Conventions",
          "",
          "- **Always use ES modules** (`import`/`export`), never `require()` (exception: `.projenrc.ts` files where `require` is allowed)",
          "- Import order:",
          "  1. Built-in Node.js modules (e.g., `node:path`, `node:fs`)",
          "  2. External dependencies (alphabetically sorted)",
          "  3. Internal imports (relative paths)",
          "- Group imports with blank lines between groups",
          "- Alphabetize imports within each group (case-insensitive)",
          "- Use absolute imports from package root when possible",
          "- Avoid duplicate imports (`import/no-duplicates`); all imports must be resolvable (`import/no-unresolved`)",
          "",
          "## Error Handling",
          "",
          "- Always handle promises properly with `await`",
          "- Use `@typescript-eslint/return-await` rule (always return await)",
          "- Never leave floating promises (`@typescript-eslint/no-floating-promises`)",
          "- Use proper error types and meaningful error messages",
          "- Do not swallow errors or use empty catch blocks",
          "- Prefer async/await over raw promises",
        ].join("\n"),
        tags: ["coding"],
      },
      {
        name: "pull-request-conventions",
        description:
          "Conventional commit PR titles, closing keywords, change summaries",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Pull Request Conventions",
          "",
          "## PR Title Prefix",
          "",
          "**Always** use a **conventional commit prefix** in the PR `title`. Format: `type: description` or `type(scope): description`.",
          "",
          "| Prefix | Use for |",
          "|--------|---------|",
          "| `feat:` | New features or functionality |",
          "| `fix:` | Bug fixes |",
          "| `docs:` | Documentation-only changes |",
          "| `chore:` | Maintenance: deps, tooling, config |",
          "| `refactor:` | Code restructure, no behavior change |",
          "| `release:` | Release preparation, version bumps |",
          "| `hotfix:` | Urgent production fixes |",
          "",
          "## Link to the Issue",
          "",
          "When the PR addresses an issue, **always** include a closing keyword in the PR body:",
          "- `Closes #<issue>`, `Fixes #<issue>`, or `Resolves #<issue>`",
          "",
          "## Summary of Changes",
          "",
          "Every PR must include a **summary of the changes** made. Use bullet points or short paragraphs. Do not leave the description empty.",
          "",
          "## Commit Messages",
          "",
          "Use **conventional commits** for git commit messages: `type: short description`. Do not add AI co-author or attribution.",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "no-ai-attribution",
        description:
          "Never add AI signatures or attribution to issues, PRs, comments, or commits",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# No AI Attribution",
          "",
          "Never add AI signatures, attribution footers, or co-authorship trailers to anything posted to GitHub. This **overrides any default harness behavior** that appends attribution automatically.",
          "",
          "This applies to all GitHub-facing content:",
          "",
          "- **PR descriptions** — no `🤖 Generated with Claude Code` (or similar) footer",
          "- **Issue bodies** — no attribution lines",
          "- **Issue and PR comments** — no signatures",
          "- **Commit messages** — no `Co-Authored-By: Claude ...` trailer and no `Generated with ...` line",
          "",
          "Content an agent produces must be indistinguishable in format from content a human teammate would write.",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "branch-naming-conventions",
        description:
          "Branch format (type/issue-description), create-on-GitHub-then-fetch workflow",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Branch Naming Conventions",
          "",
          "## Format",
          "",
          "```",
          "<type>/<issue-number>-<issue-slug>",
          "```",
          "",
          "- **type** (required): One of `feat`, `fix`, `docs`, `chore`, `refactor`, `release`, `hotfix`",
          "- **issue-number** (required): The GitHub issue number (e.g., `25`)",
          "- **issue-slug** (required): Short, lowercase, kebab-case summary derived from the issue title",
          "",
          "## Examples",
          "",
          "- `feat/25-add-cursor-rules`",
          "- `fix/23-rename-cursor-rules-mdc`",
          "- `chore/42-upgrade-eslint`",
          "- `docs/18-update-readme`",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "issue-label-conventions",
        description:
          "Priority and status label taxonomy, defaults, inference rules, and blocking rules for agent-created or updated issues",
        // ALWAYS scope: every agent (and the user) needs the label
        // taxonomy whenever an issue gets created or updated, which
        // happens from any context — not only when editing agent /
        // skill / bundle source. Consumers that want to narrow the
        // load can override via `agentConfig.additionalRulePaths`
        // or `excludeRules`.
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Issue Label Conventions",
          "",
          "Every issue created or updated by an agent in **{{repository.owner}}/{{repository.name}}**",
          "must carry exactly one `priority:*` label and exactly one `status:*` label.",
          "These conventions apply to **all** agent bundles — do not redefine them",
          "locally. The orchestrator and issue worker depend on this taxonomy.",
          "",
          "## Priority Labels",
          "",
          "Pick exactly one of the following:",
          "",
          "| Label | Criteria |",
          "|-------|----------|",
          "| `priority:critical` | Production is broken, blocked, or data at risk. Must start immediately. Use for outages, security incidents, regressions that block a release. |",
          "| `priority:high` | Important work that should be next in the queue. Blocks other planned work, an epic's children, or a near-term deliverable. |",
          "| `priority:medium` | Default for normal planned work. No immediate blocker but belongs in the current roadmap. |",
          "| `priority:low` | Nice-to-have. Can be deferred without impact. Cleanups, minor polish, non-urgent refactors. |",
          "| `priority:trivial` | Cosmetic or optional. Typos, formatting, stray comments. Safe to never pick up. |",
          "",
          "### Default Priority",
          "",
          "When the user has not indicated a priority and context provides no signal,",
          "assign `priority:medium`.",
          "",
          "### Inference Heuristics",
          "",
          "Before defaulting to medium, scan the user's request and the issue context",
          "for these signals:",
          "",
          "- **Urgency language** — words like *urgent*, *ASAP*, *immediately*, *now*,",
          "  *blocker*, *on fire* → `priority:critical`",
          "- **Importance language** — words like *important*, *soon*, *need this*,",
          "  *next up*, *before launch* → `priority:high`",
          "- **Deadlines** — an explicit due date within 7 days → `priority:high`;",
          "  within 24 hours or labeled as a release blocker → `priority:critical`",
          "- **Blocker status** — the issue blocks another open issue, an epic child,",
          "  or a release → `priority:high` (or `priority:critical` if the blocked",
          "  work is itself critical)",
          "- **Business impact** — revenue loss, customer-facing outage, security",
          "  exposure → `priority:critical`",
          "- **Polish language** — *nice to have*, *minor*, *cleanup*, *whenever*,",
          "  *low priority* → `priority:low`",
          "- **Cosmetic scope** — typo fixes, comment tweaks, formatting-only → `priority:trivial`",
          "- **Meeting-note origin** — any issue created from reviewing meeting notes or",
          "  a meeting transcript → `priority:high` (meeting follow-ups are time-sensitive",
          "  and presumed important unless the note explicitly says otherwise)",
          "",
          "### Ask vs. Infer",
          "",
          "- **Infer** when the user's request contains at least one clear signal from",
          "  the heuristics above, or when the default (`priority:medium`) is clearly",
          "  appropriate (routine feature, refactor, or chore with no urgency cues).",
          "- **Ask** before creating the issue when:",
          '  - The request mixes signals (e.g., "minor but blocks the release")',
          "  - The work would reasonably map to `priority:critical` — always confirm",
          "    before using the top tier",
          "  - The scope is ambiguous enough that medium vs. high is unclear **and**",
          "    the outcome would change how the orchestrator sequences work",
          "- When asking, pose a single question with the candidate levels, e.g.",
          '  "Is this `priority:high` (next in queue) or `priority:medium` (normal)?"',
          "",
          "## Status Labels",
          "",
          "Pick exactly one of the following. Status reflects where the issue sits in",
          "the workflow, not the nature of the work.",
          "",
          "| Label | Criteria |",
          "|-------|----------|",
          "| `status:ready` | The issue is fully specified, has no open blockers, and is available for a worker to pick up. |",
          "| `status:blocked` | The issue cannot be started yet — either it declares `Depends on: #N` on an open issue, or it is an epic with one or more open children. |",
          "| `status:in-progress` | A worker has claimed the issue and a branch exists. Set when the worker starts; cleared only when the worker opens a PR or the issue fails. |",
          "| `status:ready-for-review` | A PR has been opened for this issue and is awaiting review and merge. Replaces `status:in-progress` at the moment the PR opens. |",
          "| `status:needs-attention` | Automated triage has flagged the issue for human review (stale, failed worker, or ambiguous state). Humans reset this label manually. |",
          "| `status:deferred` | Captured for provenance only; should not be auto-dispatched. Used by consumers that want a low-priority backlog (e.g. people-profile follow-ups in research-heavy planning repos) to stay out of the orchestrator's queue until a human or a consumer-configured scheduled task promotes it. |",
          "| `status:done` | The change has shipped. Set when the PR merges and the issue closes. |",
          "",
          "### Configurable filing-site defaults",
          "",
          "The bundle-shipped `gh issue create` recipes default to",
          "`status:ready` + `priority:medium`. Consumers can override the",
          "defaults per-phase-label via",
          "`AgentConfigOptions.issueDefaults` — e.g. flip every",
          "`people:research` filing to `status:deferred` + `priority:low`",
          "without touching the bundle source. See the **`issueDefaults`**",
          "section in the configulator agent-config docs for the full",
          "schema and consumer example. `status:deferred` is a valid",
          "override value reserved for this exact use case.",
          "",
          "### The `status:deferred` invariant",
          "",
          "**No agent, procedure, or sweep ever removes `status:deferred`.",
          "Only a human promotes a deferred issue.**",
          "",
          "`status:deferred` records a deliberate human decision to park",
          "work. Automation may read it, count it, and log it — but the",
          "label is cleared exclusively by a human (or by a scheduled task",
          "a human configured for that purpose). Concretely:",
          "",
          "- **Unblock sweeps skip it.** Both `check-blocked.sh unblock`",
          "  and `unblock-dependents.sh` leave a `status:deferred` +",
          "  `status:blocked` issue blocked when its dependencies close,",
          "  emitting a `SKIP_DEFERRED #<n>` line instead of flipping to",
          "  `status:ready`. Dependency resolution is not a promotion",
          "  signal for parked work.",
          "- **Dispatch scans exclude it.** `check-blocked.sh eligible`",
          "  drops any issue carrying `status:deferred`, so a stray",
          "  `status:ready` + `status:deferred` pairing never reaches a",
          "  worker.",
          "- **No sweep writes it either.** Automation adds",
          "  `status:deferred` only at filing time via the configured",
          "  `issueDefaults` — never as a runtime triage decision.",
          "",
          "An issue that legitimately needs to leave the parked backlog is",
          "promoted by a human removing `status:deferred` and setting the",
          "appropriate base status. Any automation that removes the label",
          "is a bug.",
          "",
          "### Blocking Rules",
          "",
          "Two rules force `status:blocked` — both are non-negotiable:",
          "",
          "1. **Epic-blocking rule.** Any issue of GitHub type *Epic* that has one or",
          "   more open child issues must carry `status:blocked`. Only flip it to",
          "   `status:ready` (or close it) once every child is resolved.",
          "2. **Dependency-blocking rule.** Any issue whose body contains a",
          "   `Depends on: #N` line referencing an open issue must carry",
          "   `status:blocked` for as long as that dependency remains open.",
          "   When all referenced dependencies close, transition to `status:ready`.",
          "",
          "### Status Transitions",
          "",
          "Agents transition status labels at these well-defined points:",
          "",
          "- **Issue creation** → `status:ready` by default, or `status:blocked` if",
          "  either blocking rule applies.",
          "- **Worker claims the issue** → remove `status:ready`, add",
          "  `status:in-progress`. A branch must exist before this transition.",
          "- **Worker opens a PR** → remove `status:in-progress`, add",
          "  `status:ready-for-review`. The PR URL should be posted on the issue.",
          "- **PR merges / issue closes** → remove `status:ready-for-review`, add",
          "  `status:done`.",
          "- **Dependency resolves** → if the issue was `status:blocked` solely because",
          "  of the dependency-blocking rule, remove `status:blocked` and add",
          "  `status:ready`.",
          "- **Automated flag** → add `status:needs-attention` (do not remove existing",
          "  status). Never auto-reset a `status:needs-attention` issue back to ready;",
          "  a human must decide. The flag is **additive**: apply it with `--add-label`",
          "  alone — never pair it with a `--remove-label` that strips the base",
          "  `status:*` label. A flag that swaps out the base status (e.g. scope-gate",
          "  or worker-failure paths removing `status:ready` / `status:in-progress`)",
          "  leaves the issue with no base status — invisible to every status-driven",
          "  query and to the queue, and unrecoverable once the flag is cleared.",
          "- **Automated-flag clear** → when `status:needs-attention` is cleared, if the",
          "  issue carries no base `status:*` label (a legacy issue flagged before the",
          "  additive-flag rule, whose base status was stripped at flag time), restore",
          "  `status:ready` — or `status:blocked` if the body still declares an open",
          "  `Depends on:` dependency — in the same edit so it re-enters the workflow.",
          "- **PR CI failure** → add `status:needs-attention` to the associated PR (and",
          "  its linked issue) when CI fails in a **permanent** state — i.e. the failure",
          "  persists after a rerun, or the failure is clearly not a flake (compile",
          "  errors, failing assertions, type errors, broken builds). Do **not** flag",
          "  when CI self-mutates the PR and the follow-up run passes — for example,",
          "  lint auto-fixes, formatter commits, or snapshot/lockfile updates pushed",
          "  by CI. Those are expected self-healing behaviors, not failures.",
          "",
          "An issue must always carry exactly one of `status:ready`, `status:blocked`,",
          "`status:in-progress`, `status:ready-for-review`, or `status:done`. The",
          "`status:needs-attention` label is additive — it coexists with whichever of",
          "those five applies.",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "monorepo-layout",
        description:
          "Canonical monorepo top-level folders (docs, apps, packages, sites), the @ownerscope/<name> scoping rule, the docs-singleton carve-out, and the scaffolding skills that place new sub-projects",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Monorepo Layout",
          "",
          "Every monorepo built with `@codedrifters/configulator` uses a single,",
          "fixed top-level folder layout. Agents, scaffolding skills, pnpm-workspace",
          "globs, Turbo filters, and CI cache keys all assume these roots.",
          "",
          "## Four Canonical Top-Level Folders",
          "",
          "| Folder | Purpose | Examples |",
          "|--------|---------|----------|",
          "| `/docs` | **Single** Starlight site covering the entire monorepo. The one site that lives outside `/sites`. | Monorepo-wide developer docs, ADRs, how-to guides. |",
          "| `/apps/@scope/<name>` | Deployable applications — anything that runs or ships as a running process. | AWS CDK stacks, mobile apps (iOS/Android), backend services. |",
          "| `/packages/@scope/<name>` | Shared libraries — published npm packages and workspace-internal libraries. | `@codedrifters/configulator`, `@codedrifters/constructs`, `@codedrifters/utils`. |",
          "| `/sites/@scope/<name>` | User-facing web front ends that are **not** the monorepo-wide docs site. | Marketing sites, SPAs, per-client static sites. |",
          "",
          "No other top-level folder is part of this contract. Generated roots",
          "(`.projen/`, `.github/`, `node_modules/`, `.turbo/`, etc.) are orthogonal",
          "and unchanged.",
          "",
          "## Scoping Rule",
          "",
          "Every sub-project under `apps/`, `packages/`, or `sites/` is named",
          "`@ownerscope/<name>`, where **scope is the owning party** (not a category).",
          "",
          "- `@codedrifters/configulator` — a shared library owned by CodeDrifters → `packages/@codedrifters/configulator`",
          "- `@codedrifters/infrastructure-stack` — an AWS CDK app owned by CodeDrifters → `apps/@codedrifters/infrastructure-stack`",
          "- `@clientA/iphone-app` — a mobile app owned by Client A → `apps/@clientA/iphone-app`",
          "- `@clientB/marketing-site` — a marketing site owned by Client B → `sites/@clientB/marketing-site`",
          "",
          "The scope identifies **who owns the sub-project**, not what kind of",
          "sub-project it is. Kind is captured by the top-level folder (`apps/`,",
          "`packages/`, `sites/`). Never scope by category (e.g. `@apps/foo`,",
          "`@libs/bar`) — the top-level folder already carries that information.",
          "",
          "## Docs-Singleton Carve-Out",
          "",
          "There is **exactly one** Starlight docs site per monorepo, and it lives",
          "at `/docs`. It is **not** `sites/@scope/docs`.",
          "",
          "- The docs site is monorepo-wide. Sub-project documentation belongs",
          `  inside that single site (under \`${paths.docsRoot}/\`), not in a`,
          "  per-package `docs/` folder.",
          "- Configulator's `StarlightProject` defaults its `outdir` to `/docs` and",
          '  its `role` to `"docs"`. A second `StarlightProject` with `role: "docs"`',
          "  fails synth — the singleton rule is enforced at validate time.",
          "- Additional Starlight sites (client-facing, marketing, etc.) must set",
          '  `role: "site"`, which routes them under `sites/<scope>/<name>` just',
          "  like a plain `AstroProject`.",
          "- `/sites` is reserved for end-user-facing web front ends (marketing",
          "  sites, SPAs, client-facing apps). It deliberately does not host the",
          "  dev-docs site.",
          "",
          "## Project-Type → `outdir` Mapping",
          "",
          "Configulator project types default their `outdir` according to the",
          "following table. Override `outdir` explicitly only when you have a",
          "genuine reason — the defaults cover the common path.",
          "",
          "| Configulator project type | Default `outdir` |",
          "|---------------------------|------------------|",
          "| `TypeScriptProject` | `packages/<scope>/<name>` |",
          "| `AwsCdkProject` | `apps/<scope>/<name>` |",
          "| `AstroProject` | `sites/<scope>/<name>` |",
          "| `StarlightProject` (role = docs, default) | `docs/` |",
          "| `StarlightProject` (role = site) | `sites/<scope>/<name>` |",
          "",
          "`<scope>` is parsed from the sub-project's package name (everything",
          "after `@` and before `/`). `<name>` is the unscoped part of the",
          "package name.",
          "",
          "## Placing New Sub-Projects",
          "",
          "Never hand-roll a sub-project path. Use one of the scaffolding skills,",
          "which take `@scope/<name>` as input, validate it, and place the new",
          "project in the correct folder:",
          "",
          "- `/create-package` — scaffolds a new `TypeScriptProject` under `packages/<scope>/<name>`",
          "- `/create-app` — scaffolds a new `AwsCdkProject` under `apps/<scope>/<name>`",
          "- `/create-site` — scaffolds a new `AstroProject` under `sites/<scope>/<name>`",
          "",
          "The monorepo-wide docs site at `/docs` is a singleton and is created",
          "once per repo via `StarlightProject` — it is not scaffolded per",
          "sub-project.",
        ].join("\n"),
        tags: ["project"],
      },
      {
        name: "source-quality-verification",
        description:
          "Source tier hierarchy (T1–T4), conflict resolution, temporal validity, and source-annotation format for research-producing agents",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Source Quality & Verification",
          "",
          "All research-producing agents must follow this source hierarchy when",
          "writing profiles and reference documents. The tiers govern which",
          "source to prefer when claims conflict and when time-sensitive claims",
          "need re-verification.",
          "",
          "## Source Tier Hierarchy",
          "",
          "### T1 — Primary Living",
          "",
          "Authoritative sources that update when reality changes.",
          "Use these for current-state claims. Examples:",
          "",
          "- Official product docs and API references",
          "- Company about / leadership / team pages",
          "- Live government registries (SEC EDGAR, Companies House)",
          "- Current-role LinkedIn profiles",
          "- Investor relations pages",
          "",
          "### T2 — Primary Snapshot",
          "",
          "Authoritative but time-stamped sources straight from the subject.",
          "Trust for current state only when fresh (< 6 months) and no T1",
          "source contradicts them. Examples:",
          "",
          "- Press releases from the entity",
          "- Earnings transcripts and investor calls",
          "- Regulatory filings (10-K, 10-Q, 8-K)",
          "- Official announcements and conference presentations",
          "",
          "### T3 — Secondary",
          "",
          "Third-party sources that interpret or re-report primary material.",
          "Good for context and triangulation; T1 wins on conflict. Examples:",
          "",
          "- News articles and trade press",
          "- Industry reports and analyst research",
          "- Database aggregators (Crunchbase, PitchBook, Owler)",
          "",
          "### T4 — Self-Reported / Marketing",
          "",
          "Promotional or unverified sources. Use for discovery and leads only",
          "— never as the sole source for a factual claim, and always note the",
          "self-reported nature in the annotation. Examples:",
          "",
          "- Vendor comparison pages and customer testimonials on vendor sites",
          "- Social-media posts",
          "- Job postings",
          "- Wikipedia",
          "",
          "## Conflict Resolution",
          "",
          "Higher tier wins. Within the same tier, more recent wins. If a newer",
          "T2 source contradicts an older T1 source on a time-sensitive claim,",
          "flag for re-verification — the T1 source may simply not have been",
          "updated yet.",
          "",
          "## Temporal Validity",
          "",
          "Some claims decay faster than others. When a fast-decay claim comes",
          "from a source older than 6 months, agents **must** attempt to",
          "corroborate it with a T1 living source. If no T1 source is available,",
          "add an inline note: `_(source: YYYY-MM, may need re-verification)_`.",
          "",
          "| Decay | Re-verify if source older than | Typical claims |",
          "|-------|-------------------------------|----------------|",
          "| **Fast** | 6 months | Leadership roles, employee count, pricing, product features, funding status, office locations |",
          "| **Slow** | 18 months | Founding year, ownership structure, core business description, industry classification, HQ location |",
          "| **Stable** | Verify once, note source date | Historical facts (acquisition dates, founding story), published financials for a specific period |",
          "",
          "## Source Annotation Format",
          "",
          "In the `## Sources` section of profiles and reference documents,",
          "annotate each source with its tier and date:",
          "",
          "```",
          "1. [Company Leadership Page](https://...) — T1, accessed 2026-04",
          "2. [Company press release](https://...) — T2, published 2026-02",
          "3. [TechCrunch coverage](https://...) — T3, published 2025-11",
          "```",
          "",
          "Use `accessed YYYY-MM` for living sources (T1) and `published YYYY-MM`",
          "for snapshot sources (T2, T3, T4).",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "issue-conventions",
        description:
          "Issue title prefixes, GitHub issue type mapping, prerequisite issues",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Issue Title Conventions",
          "",
          "## Format",
          "",
          "```",
          "<type>: <description>",
          "```",
          "",
          "## Types",
          "",
          "| Prefix | Use for |",
          "|--------|---------|",
          "| `epic:` | Large initiatives spanning multiple child issues |",
          "| `feat:` | New features or functionality |",
          "| `fix:` | Bug fixes |",
          "| `chore:` | Maintenance: deps, tooling, config |",
          "| `docs:` | Documentation-only work |",
          "| `refactor:` | Code restructure, no behavior change |",
          "| `release:` | Release preparation, version bumps |",
          "| `hotfix:` | Urgent production fixes |",
          "",
          ...renderGithubIssueTypeSectionLines(),
          "",
          "## Prerequisite Issues",
          "",
          "Include any prerequisite (blocking) issues in the issue body when they exist.",
          "Use a **Dependencies** section or `**Depends on:** #123`.",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "progress-file-convention",
        description:
          "Progress-file schema and write rules, partial-resume protocol, stale-branch decision tree, and [BLOCKED] comment format that let phased agents survive crashes without losing work.",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        // Bundle defaults exclude paths into configulator's own source
        // (only meaningful when configulator is a workspace package,
        // i.e. in codedrifters/packages itself). That repo restores
        // them via `agentConfig.additionalRulePaths`; other consumers
        // get a clean default.
        filePatterns: [".claude/agents/*.md", ".claude/procedures/**"],
        content: renderProgressFilesRuleContent(conventions.progressFiles),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
      {
        name: "shared-editing-safety",
        description:
          "Shared-editing safety: single-entry deterministic-sort inserts on index files, commit-path verification, and the merge-conflict resolution recipe that keeps concurrent agent sessions from dropping each other's rows on shared registries and feature matrices.",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        filePatterns: DEFAULT_SHARED_INDEX_PATHS,
        content: renderSharedEditingRuleContent(conventions.sharedEditing),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
      {
        name: "temporal-framing-convention",
        description:
          "Temporal-framing convention: every agent-authored time-sensitive factual claim (ownership, leadership tenure, regulatory status, litigation, dated metrics) carries an inline `as of [YYYY-MM-DD]` or `as of [Month YYYY]` qualifier on first occurrence so refresh agents have a mechanical signal for which claims to re-verify.",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        filePatterns: [
          "docs/src/content/docs/profiles/**/*.md",
          "docs/src/content/docs/industry-research/**/*.md",
          "docs/src/content/docs/software-research/**/*.md",
          "docs/src/content/docs/regulatory-research/**/*.md",
          "docs/src/content/docs/standards-research/**/*.md",
          "docs/src/content/docs/customer-research/**/*.md",
        ],
        content: renderTemporalFramingRuleContent(conventions.temporalFraming),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
      {
        name: "skill-evals",
        description:
          "Skill eval harness contract: declarative prompt/expected-output regression suites per skill at `<skillsRoot>/<skill-name>/evals/evals.json`, parameterised by a shared product-context fixture so the same eval shape works across every configulator-consuming project without forking fixtures.",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        // Bundle defaults exclude paths into configulator's own
        // source — see comment on progress-file-convention above.
        filePatterns: [".claude/skills/**"],
        content: renderSkillEvalsRuleContent(conventions.skillEvals),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
      {
        name: "issue-templates-convention",
        description: `Issue-templates convention: a single hand-authored reference page under \`${paths.docsRoot}/agents/issue-templates.md\` that carries one canonical \`gh issue create\` recipe per downstream phase label, and the reference-don't-inline rule that keeps bundle rules and agent prompts citing that page instead of duplicating full template invocations.`,
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        // Bundle defaults exclude paths into configulator's own
        // source — see comment on progress-file-convention above.
        filePatterns: [
          ".claude/skills/**/SKILL.md",
          ".claude/agents/*.md",
          `${paths.docsRoot}/agents/issue-templates.md`,
        ],
        content: renderIssueTemplatesRuleContent(
          conventions.issueTemplates,
          conventions.hasDownstreamIssueKindBundles,
        ),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
  };
}

/**
 * Default-paths instance of the base bundle, preserved for backward
 * compatibility with consumers that import the const directly. The
 * factory above is the canonical entry point when a consumer supplies
 * `AgentConfigOptions.paths`.
 */
export const baseBundle: AgentRuleBundle = buildBaseBundle();
